import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Package, Clock, MapPin, ArrowUpRight } from 'lucide-react';
import { getSupplierById, PRODUCTS } from '../data/mockData';
import { useProduct } from '../data/hooks';
import SupplierCard from '../components/SupplierCard';
import { LoadingState, ErrorState } from '../components/states/AsyncStates';
import { track, EVENTS } from '../lib/analytics';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import Img from '../components/Img';

const ProductDetail = () => {
  const { slug } = useParams();
  const { data: product, loading, error, refetch } = useProduct(slug);

  useEffect(() => {
    if (!product) return;
    track(EVENTS.PRODUCT_VIEWED, {
      slug: product.slug,
      category: product.category,
      supplier_count: (product.suppliers || []).length,
      moq: product.moq,
    });
  }, [product?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="mx-auto max-w-2xl px-5 py-24"><LoadingState label="Loading product…" /></div>;
  }
  if (error) {
    return <div className="mx-auto max-w-2xl px-5 py-24"><ErrorState description={error?.message} onRetry={refetch} /></div>;
  }
  if (!product) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="product-not-found">
        <div className="label-overline">404</div>
        <h1 className="mt-4 font-display text-4xl tracking-tighter">Product not found</h1>
        <Link to="/products" className="mt-6 inline-block btn-giwahs btn-giwahs-primary">Back to directory</Link>
      </div>
    );
  }

  const matched = (product.suppliers || []).map(getSupplierById).filter(Boolean);
  const related = PRODUCTS.filter(p => p.category === product.category && p.slug !== product.slug).slice(0, 4);

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Products', url: '/products' },
    { name: product.name, url: `/product/${product.slug}` },
  ];

  return (
    <div data-testid="product-detail-page" className="surface-obsidian text-platinum">
      <SEO
        title={`${product.name} — ${product.suppliers.length} Verified Suppliers`}
        description={`${product.name}. MOQ ${product.moq} units, lead time ${product.leadTime}, estimated wholesale ${product.pricing}. Sourcing regions: ${product.sourcingRegions.join(', ')}.`}
        image={product.image}
        type="product"
        breadcrumbs={breadcrumbItems}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          image: product.image,
          description: `${product.name} — ${product.tags.join(', ')}`,
          category: product.category.replace('-', ' '),
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'USD',
            lowPrice: product.pricing.split('–')[0].replace(/[^\d.]/g, ''),
            highPrice: product.pricing.split('–')[1]?.replace(/[^\d.]/g, '') || product.pricing.split('–')[0].replace(/[^\d.]/g, ''),
            offerCount: String(product.suppliers.length),
          },
        }}
      />
      {/* Breadcrumbs */}
      <div className="border-b border-hairline bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1440px] px-5 py-3 md:px-8">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 md:py-14">
          <Link to="/products" className="inline-flex items-center gap-2 label-overline text-platinum-2 hover:text-gold" data-testid="product-back-btn">
            <ArrowLeft size={14} strokeWidth={1.5} /> Back to products
          </Link>
          <div className="mt-8 grid gap-10 md:grid-cols-12">
            <div className="md:col-span-6">
              <div className="aspect-[4/5] overflow-hidden border border-black bg-neutral-100">
                <Img src={product.image} alt={`${product.name} — production reference image`} variant="heroNarrow" priority className="h-full w-full object-cover" />
              </div>
            </div>
            <div className="md:col-span-6">
              <span className="label-overline">{product.category.replace('-', ' ')}</span>
              <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem, 5vw, 4rem)', lineHeight: 0.95 }}>
                {product.name}
              </h1>
              <div className="mt-6 flex flex-wrap gap-2">
                {product.tags.map(t => <span key={t} className="giwahs-chip-dark font-mono-data text-xs">{t}</span>)}
              </div>
              <div className="mt-8 grid grid-cols-3 gap-px bg-neutral-200">
                <div className="bg-white p-4"><div className="label-overline text-neutral-500">MOQ</div><div className="mt-2 font-display text-2xl tracking-tighter" style={{ fontWeight: 600 }}>{product.moq}</div></div>
                <div className="bg-white p-4"><div className="label-overline text-neutral-500">Lead Time</div><div className="mt-2 font-display text-2xl tracking-tighter" style={{ fontWeight: 600 }}>{product.leadTime.split(' ')[0]}d</div></div>
                <div className="bg-white p-4"><div className="label-overline text-neutral-500">Est. Price</div><div className="mt-2 font-mono-data text-sm">{product.pricing}</div></div>
              </div>
              <div className="mt-6">
                <div className="label-overline text-neutral-500">Sourcing Regions</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {product.sourcingRegions.map(r => (
                    <span key={r} className="giwahs-chip-dark font-mono-data text-xs">
                      <MapPin size={11} strokeWidth={1.5} /> {r}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={`/rfq?product=${product.slug}`} data-testid="product-rfq-btn" className="btn-giwahs btn-giwahs-primary">Request Quote</Link>
                <Link to="/factories" className="btn-giwahs btn-giwahs-secondary">Browse Similar Factories</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <span className="label-overline">/ Matched Suppliers</span>
          <h2 className="mt-4 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            {matched.length} factories produce this.
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {matched.map((s, i) => <SupplierCard key={s.id} supplier={s} index={i} />)}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="surface-obsidian text-platinum">
          <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
            <span className="label-overline">/ Related Products</span>
            <h3 className="mt-4 font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>You may also explore</h3>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {related.map(p => (
                <Link key={p.id} to={`/product/${p.slug}`} className="group flex flex-col border border-black bg-white">
                  <div className="aspect-[4/5] overflow-hidden bg-neutral-100">
                    <Img src={p.image} alt={`${p.name} — related ${p.category.replace('-', ' ')}`} variant="card" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <div className="font-display text-base" style={{ fontWeight: 600 }}>{p.name}</div>
                    <div className="mt-2 inline-flex items-center gap-1 label-overline">View <ArrowUpRight size={11} strokeWidth={1.5} /></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
